import React, { useLayoutEffect } from "react";

// Hand-drawn icon set for the mobile app (style "A", approved 2026-10-07):
// a pen line over a soft square tile in the category's color. Replaces
// emoji on mobile only. Desktop still renders emoji from the same maps.
//
// The data maps (CATEGORY_EMOJI, goal/badge emoji, notification icons) are
// untouched: they stay emoji strings, and <Glyph> translates an emoji to its
// drawing at render time. An emoji with no drawing renders as itself, so a
// client-picked goal emoji we never drew still shows up.
//
// The ink is `currentColor`, so a glyph inherits the text color around it
// and works in both themes. Sizes are in `em`, so the existing `fontSize`
// on each emoji's wrapper keeps sizing the drawing.

type Wash = "honey" | "sage" | "clay" | "sky" | "lav";

const WASH: Record<Wash, string> = {
  honey: "#EEC988",
  sage: "#B9D9A5",
  clay: "#F0B49A",
  sky: "#B7D3E8",
  lav: "#D4C6EC",
};

// 24×24 pen paths. Kept as markup strings so the set reads as one sheet.
const PATHS: Record<string, string> = {
  folder: '<path d="M3 7.5Q3 6 4.5 6H9l2 2h8.5Q21 8 21 9.5v8q0 1.5-1.5 1.5h-15Q3 19 3 17.5Z"/><path d="M3 10.5h18"/>',
  dining: '<path d="M6 3v5q0 2.5 2 2.5T10 8V3M8 10.5V21M16.5 21V3q-3 2-3 7.5 0 2 3 2.5"/>',
  coffee: '<path d="M5 9h11v5q0 5-5.5 5T5 14Z"/><path d="M16 10.5q4 0 3.5 2.5T16 15M8.5 6.5q1-1.5 0-3M12 6.5q1-1.5 0-3"/>',
  cart: '<path d="M2.5 4H5l2.5 11H18l2-7H6"/><circle cx="9" cy="19" r="1.5"/><circle cx="17" cy="19" r="1.5"/>',
  fuel: '<path d="M5 20V5q0-1.5 1.5-1.5h6Q14 3.5 14 5v15M3.5 20h12M7.5 7.5h4v3h-4ZM14 9.5h2q1.5 0 1.5 1.5v5q0 1.5 1.5 1.5t1.5-1.5V8L18 5.5"/>',
  film: '<path d="M4 10h16v9.5q0 1-1 1H5q-1 0-1-1ZM4 10l-.6-3.2q-.2-1 .8-1.2l14-2.6q1-.2 1.2.8l.4 2.2M8 5.2l2 4.3M13 4.3l2 4.4"/>',
  bulb: '<path d="M12 3Q6 3 6 9q0 3 3 5v3h6v-3q3-2 3-5 0-6-6-6Z"/><path d="M9.5 20h5M10 11l2 2 2-2"/>',
  bag: '<path d="M5 8h14l-1 12.5H6ZM9 10V6.5Q9 3.5 12 3.5t3 3V10"/>',
  medical: '<path d="M4.5 6.5q0-2 2-2h11q2 0 2 2v11q0 2-2 2h-11q-2 0-2-2ZM12 8v8M8 12h8"/>',
  plane: '<path d="M21 4 3 11l7 2.5L12.5 21 21 4ZM10 13.5 21 4"/>',
  phone: '<path d="M7.5 3.5h9Q18 3.5 18 5v14q0 1.5-1.5 1.5h-9Q6 20.5 6 19V5q0-1.5 1.5-1.5ZM10.5 17.5h3"/>',
  bank: '<path d="M3.5 9.5 12 4l8.5 5.5ZM5.5 10.5v7M10 10.5v7M14 10.5v7M18.5 10.5v7M3.5 20h17"/>',
  shield: '<path d="M12 3.5 19 6v5.5q0 6-7 9-7-3-7-9V6Z"/><path d="M9 12l2 2 4-4"/>',
  tooth: '<path d="M7 4Q4 4 4 8q0 3 1.5 5.5L7 20q.5 1 1.5 0l1.5-5q2-1.5 4 0l1.5 5q1 1 1.5 0l1.5-6.5Q20 11 20 8q0-4-3-4-2 0-3 1-1 .7-2 0-1-1-3-1Z"/>',
  car: '<path d="M3 16v-3q0-2 2-2l2-4h9l3 4q2 0 2 2v3Z"/><circle cx="7.5" cy="16.5" r="2"/><circle cx="16.5" cy="16.5" r="2"/>',
  book: '<path d="M4 5q4-1.5 8 1 4-2.5 8-1v14q-4-1.5-8 1-4-2.5-8-1ZM12 6v14"/>',
  house: '<path d="M3.5 11 12 4l8.5 7"/><path d="M6 9.5V20h12V9.5M10 20v-5h4v5"/>',
  bottle: '<path d="M10 3h4v3.5l1.5 2V20q0 .5-.5.5H9q-.5 0-.5-.5V8.5L10 6.5ZM8.5 12h7"/>',
  lotus: '<path d="M12 19.5q-4-3-4-8 2 1 4 4 2-3 4-4 0 5-4 8ZM12 19.5q-6 0-8.5-6 3-.5 5 1.5M12 19.5q6 0 8.5-6-3-.5-5 1.5"/>',
  dumbbell: '<path d="M3 12h18M6 7.5v9M4 9.5v5M18 7.5v9M20 9.5v5"/>',
  rattle: '<circle cx="9" cy="9" r="5"/><path d="M12.5 12.5 19 19M17.5 20.5l3-3M7.5 11q1.5 1 3 0"/>',
  paw: '<path d="M12 13q-4 0-5 4.5Q7 20 9.5 20h5q2.5 0 2.5-2.5-1-4.5-5-4.5Z"/><circle cx="6" cy="10" r="1.6"/><circle cx="9.5" cy="6.5" r="1.6"/><circle cx="14.5" cy="6.5" r="1.6"/><circle cx="18" cy="10" r="1.6"/>',
  heart: '<path d="M12 20s-8-4.5-8-10.5Q4 6 7.5 6 10 6 12 8.5 14 6 16.5 6 20 6 20 9.5 20 15.5 12 20Z"/>',
  dome: '<path d="M4 20h16M5.5 18v-6M9.5 18v-6M14.5 18v-6M18.5 18v-6M4 11h16M6 11q0-6 6-6t6 6M12 5V3"/>',
  key: '<circle cx="8" cy="8" r="4"/><path d="M11 11l9 9M16.5 16.5l2-2M18.5 18.5l2-2"/>',
  wrench: '<path d="M15 3.5q-3 1-2.5 4.5L4.5 16q-1.5 1.5 0 3t3 0l8-8q3.5.5 4.5-2.5l-2.5.5-2-2Z"/>',
  scales: '<path d="M12 4v16M8 20h8M5 7h14M5 7l-2.5 6q2.5 2 5 0ZM19 7l-2.5 6q2.5 2 5 0Z"/>',
  briefcase: '<path d="M3.5 8.5Q3.5 7 5 7h14q1.5 0 1.5 1.5v10Q20.5 20 19 20H5q-1.5 0-1.5-1.5ZM9 7V5.5Q9 4 10.5 4h3Q15 4 15 5.5V7M3.5 13h17"/>',
  cash: '<path d="M3 7h18v10H3Z"/><circle cx="12" cy="12" r="2.5"/><path d="M6 10v4M18 10v4"/>',
  moneybag: '<path d="M9 4h6l-1.5 3h-3ZM10.5 7Q4 10 4 15.5 4 20 8 20h8q4 0 4-4.5Q20 10 13.5 7M12 11v6"/>',
  gift: '<path d="M4 9h16v3.5H4ZM5.5 12.5V20h13v-7.5M12 9v11M12 9Q8 9 8 6.5T12 9q4 0 4-2.5T12 9"/>',
  target: '<circle cx="11" cy="13" r="8"/><circle cx="11" cy="13" r="4.5"/><circle cx="11" cy="13" r="1.2"/><path d="M11 13 19.5 4.5M17 4.5h2.5V7"/>',
  card: '<path d="M3 6.5Q3 5 4.5 5h15Q21 5 21 6.5v11q0 1.5-1.5 1.5h-15Q3 19 3 17.5ZM3 10h18M6.5 15h4"/>',
  chart: '<path d="M3.5 4v16h17"/><path d="M6.5 15l4-4 3 3 6-6.5M16 7.5h3.5V11"/>',
  undo: '<path d="M9 5 4.5 9.5 9 14M4.5 9.5H15q5 0 5 5t-5 5h-3"/>',
  cycle: '<path d="M5 12q0-6 7-6.5 4 0 6 3.5M19 12q0 6-7 6.5-4 0-6-3.5M18 4.5V9h-4.5M6 19.5V15h4.5"/>',
  music: '<path d="M9 17V5l10-2v12"/><circle cx="6.5" cy="17" r="2.5"/><circle cx="16.5" cy="15" r="2.5"/>',
  parking: '<path d="M4.5 6.5q0-2 2-2h11q2 0 2 2v11q0 2-2 2h-11q-2 0-2-2ZM10 16.5v-9h3q2.5 0 2.5 2.5T13 12.5h-3"/>',
  trophy: '<path d="M7 4h10v5q0 5-5 5T7 9Z"/><path d="M7 6H4.5q-.5 4 3 4.5M17 6h2.5q.5 4-3 4.5M12 14v3M9 17h6v3H9Z"/>',
  party: '<path d="M4 20 8 9l7 7Z"/><path d="M13 3.5l1 2M18.5 5.5l-2 1.5M20.5 11l-2.5.5M10.5 6.5q1.5 1 .5 2.5"/><circle cx="17" cy="9" r=".4"/>',
  sparkle: '<path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8ZM18.5 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7Z"/>',
  plant: '<path d="M12 21V10"/><path d="M12 13Q6 13 5 7q6 0 7 6ZM12 10q1-6 7-6-1 6-7 6Z"/>',
  clip: '<path d="M15.5 7v9q0 4-3.7 4T8 16V6q0-2.5 2.5-2.5T13 6v9q0 1.5-1.5 1.5T10 15V8"/>',
  scissors: '<circle cx="6" cy="7" r="2.6"/><circle cx="6" cy="17" r="2.6"/><path d="M8.2 8.5 20 17M8.2 15.5 20 7"/>',
  no: '<circle cx="12" cy="12" r="8"/><path d="M6.5 6.5l11 11"/>',
  flame: '<path d="M12 3q5 5 5 10.5T12 20t-5-6q0-3.5 2.5-5 0 3 1.5 3.5Q10 7 12 3Z"/>',
  doc: '<path d="M6 3.5h8l4 4V20q0 .5-.5.5h-11Q6 20.5 6 20ZM14 3.5V8h4M9 12h6M9 15.5h6"/>',
  picture: '<path d="M3.5 5.5h17v13h-17ZM3.5 16l5-5 4 4 2.5-2.5 5.5 5"/><circle cx="15.5" cy="9" r="1.5"/>',
  sheet: '<path d="M4 4h16v16H4ZM4 9.5h16M4 15h16M10 4v16"/>',
  notepad: '<path d="M6 4h12v16.5H6ZM9 3v3M12 3v3M15 3v3M9 10h6M9 13.5h6M9 17h3.5"/>',
  plug: '<path d="M9 3v5M15 3v5M6.5 8h11v3q0 5-5.5 5T6.5 11ZM12 16v5"/>',
  pen: '<path d="M4 20l1-4.5L16 4.5q1.5-1.5 3 0t0 3L8 18.5ZM14 7l3 3M4 20h7"/>',
  check: '<path d="M5 12.5 10 17l9-10.5"/>',
  medal: '<path d="M8 3l2.5 6M16 3l-2.5 6"/><circle cx="12" cy="14.5" r="5.5"/><path d="M12 12l.8 1.7 1.8.2-1.3 1.2.4 1.8-1.7-1-1.7 1 .4-1.8-1.3-1.2 1.8-.2Z"/>',
};

export type HandIconName = keyof typeof PATHS;
export type { Wash };
export { WASH };

// Default wash per drawing. Food reads honey, home clay, getting around sky,
// money sage, feelings/occasions lavender.
const DEFAULT_WASH: Record<string, Wash> = {
  folder: "honey", dining: "honey", coffee: "honey", cart: "sage", fuel: "sky",
  film: "lav", bulb: "honey", bag: "clay", medical: "clay", plane: "sky",
  phone: "sky", bank: "sage", shield: "sky", tooth: "sky", car: "sky",
  book: "lav", house: "clay", bottle: "sky", lotus: "lav", dumbbell: "clay",
  rattle: "honey", paw: "honey", heart: "clay", dome: "sage", key: "honey",
  wrench: "sky", scales: "lav", briefcase: "honey", cash: "sage", moneybag: "sage",
  gift: "clay", target: "clay", card: "sky", chart: "sage", undo: "sky",
  cycle: "sky", music: "lav", parking: "sky", trophy: "honey", party: "lav",
  sparkle: "honey", plant: "sage", clip: "sky", scissors: "sky", no: "clay",
  flame: "clay", doc: "honey", picture: "sky", sheet: "sage", notepad: "honey",
  plug: "lav", pen: "lav", check: "sage", medal: "honey",
};

// Emoji → drawing. Variation selectors (U+FE0F) are stripped before lookup,
// so "🛍️" and "🛍" both land here.
const EMOJI_TO_ICON: Record<string, HandIconName> = {
  "📂": "folder", "📁": "folder", "🍔": "dining", "🍽": "dining", "☕": "coffee",
  "🛒": "cart", "⛽": "fuel", "🎬": "film", "💡": "bulb", "🛍": "bag",
  "🏥": "medical", "✈": "plane", "📱": "phone", "🏦": "bank", "🛡": "shield",
  "🦷": "tooth", "🚗": "car", "🚘": "car", "📚": "book", "🏠": "house", "🏡": "house",
  "🧴": "bottle", "💆": "lotus", "🏋": "dumbbell", "👶": "rattle", "🐾": "paw",
  "❤": "heart", "🏛": "dome", "🔑": "key", "🔧": "wrench", "🛠": "wrench",
  "⚖": "scales", "💼": "briefcase", "💵": "cash", "💰": "moneybag", "🎁": "gift",
  "🎯": "target", "💳": "card", "📈": "chart", "↩": "undo", "🔄": "cycle",
  "🎵": "music", "🅿": "parking", "🏆": "trophy", "🎉": "party", "🎊": "party",
  "👏": "sparkle", "🌿": "plant", "📎": "clip", "✂": "scissors", "🚫": "no",
  "🔥": "flame", "📄": "doc", "🖼": "picture", "📊": "sheet", "📝": "notepad",
  "🔌": "plug", "✍": "pen", "✅": "check", "🏅": "medal",
};

const DEFS_ID = "hand-icon-defs";

// One shared pair of SVG filters for every glyph on the page, injected once.
// `wob` nudges the pen line so it reads as drawn.
export function ensureHandDefs() {
  if (typeof document === "undefined" || document.getElementById(DEFS_ID)) return;
  const wrap = document.createElement("div");
  wrap.innerHTML = `<svg id="${DEFS_ID}" width="0" height="0" style="position:absolute" aria-hidden="true">
    <filter id="hand-wob"><feTurbulence type="fractalNoise" baseFrequency=".06" numOctaves="2" seed="3"/><feDisplacementMap in="SourceGraphic" scale="1.3"/></filter>
  </svg>`;
  document.body.appendChild(wrap.firstChild as Node);
}

export function HandIcon({
  name, size = "1.25em", wash, noWash, title, style,
}: {
  name: HandIconName;
  size?: number | string;
  wash?: Wash;
  noWash?: boolean;
  title?: string;
  style?: React.CSSProperties;
}) {
  useLayoutEffect(ensureHandDefs, []);
  const tint = WASH[wash || DEFAULT_WASH[name] || "honey"];
  // A clean square tile with the app's 4px corners. The first version used a
  // turbulence-warped blot, which drew a different lumpy shape behind every
  // icon and read as messy at list size (2026-10-07).
  const markup = `${noWash ? "" : `<rect x=".5" y=".5" width="23" height="23" rx="4" fill="${tint}" opacity=".6"/>`}<g fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" filter="url(#hand-wob)">${PATHS[name]}</g>`;
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      style={{ display: "inline-block", verticalAlign: "-0.25em", flexShrink: 0, overflow: "visible", ...style }}
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
}

export function handIconFor(emoji: string | null | undefined): HandIconName | null {
  if (!emoji) return null;
  return EMOJI_TO_ICON[emoji.replace(/️/g, "").trim()] || null;
}

/** Render an emoji string as its hand-drawn version, or as itself when there
 *  is no drawing for it. Drop-in for `{someEmoji}` in mobile JSX. */
export function Glyph({ e, size, style }: { e: string | null | undefined; size?: number | string; style?: React.CSSProperties }) {
  const name = handIconFor(e);
  if (name) return <HandIcon name={name} size={size} style={style} />;
  return <>{e}</>;
}
